import { BotaoVoltar } from "@/shared/components/BotaoVoltar";
import { useState } from "react";
import { Check, CircleCheck, CircleX, ShieldCheck, X } from "lucide-react";
import { Modal } from "@/shared/components/Modal";
import { Avatar } from "@/modules/escala/components/Avatar";
import { BlocoData } from "@/modules/escala/components/BlocoData";
import { Pill } from "@/modules/escala/components/Pill";
import { formatarHora } from "@/modules/escala/format";

type RespostaEscolhida = "CONFIRMADA" | "RECUSADA";

interface ResponderEscalaProps {
  aberto: boolean;
  /** "própria": a pessoa responde por si mesma. "administrativa": liderança registra em nome de quem tem dificuldade de usar o sistema. */
  modo: "propria" | "administrativa";
  /** Só usado no modo administrativo, para deixar claro de quem é a resposta. */
  nomeParticipante?: string;
  /** Função da pessoa que responde (a própria, ou o participante no modo administrativo). */
  funcaoParticipante?: string | null;
  escalaNome: string;
  escalaDataHora: string;
  ministerioNome: string;
  carregando: boolean;
  erro: string | null;
  /** Preenchido quando a resposta já foi gravada: troca o formulário pela tela de resultado. */
  resultado: RespostaEscolhida | null;
  onResponder: (resposta: RespostaEscolhida, justificativa: string | null) => void;
  onFechar: () => void;
}

/**
 * Aceitar/recusar (própria) ou registrar resposta administrativa — mesmo
 * fluxo de UI para os dois casos (só muda o título e qual RPC o chamador
 * aciona), já que backend/regras são idênticos: RECUSADA sempre exige
 * justificativa (o backend já rejeita sem ela; a validação aqui é só UX).
 *
 * Opção B da proposta de UX (cartões de escolha empilhados): dois
 * `<input type="radio">` reais — teclado e leitor de tela funcionam sem
 * código extra — estilizados como cartões.
 */
export function ResponderEscala({
  aberto,
  modo,
  nomeParticipante,
  funcaoParticipante,
  escalaNome,
  escalaDataHora,
  ministerioNome,
  carregando,
  erro,
  resultado,
  onResponder,
  onFechar,
}: ResponderEscalaProps) {
  const [escolha, setEscolha] = useState<RespostaEscolhida | null>(null);
  const [justificativa, setJustificativa] = useState("");
  const [justificativaTocada, setJustificativaTocada] = useState(false);

  const administrativa = modo === "administrativa";
  const primeiroNome = nomeParticipante?.split(" ")[0] ?? "a pessoa";
  const faltaJustificativa = escolha === "RECUSADA" && !justificativa.trim();
  const titulo = administrativa && nomeParticipante ? `Registrar resposta de ${nomeParticipante}` : "Responder à escala";

  function fechar() {
    setEscolha(null);
    setJustificativa("");
    setJustificativaTocada(false);
    onFechar();
  }

  function enviar() {
    if (!escolha || faltaJustificativa) return;
    onResponder(escolha, escolha === "RECUSADA" ? justificativa.trim() : null);
  }

  const rotuloBotao = !escolha
    ? "Escolha uma resposta"
    : escolha === "CONFIRMADA"
      ? administrativa
        ? "Registrar confirmação"
        : "Confirmar presença"
      : administrativa
        ? "Registrar recusa"
        : "Enviar recusa";

  if (resultado) {
    const confirmada = resultado === "CONFIRMADA";
    const tituloResultado = administrativa
      ? confirmada
        ? "Confirmação registrada"
        : "Recusa registrada"
      : confirmada
        ? "Presença confirmada"
        : "Recusa enviada";
    const textoResultado = administrativa
      ? `A resposta de ${nomeParticipante ?? "a pessoa"} foi registrada como feita pela liderança.`
      : confirmada
        ? "Sua presença está registrada nesta escala."
        : "A liderança do ministério foi avisada da sua recusa.";
    return (
      <Modal titulo={titulo} aberto={aberto} onClose={fechar}>
        <div className="flex flex-col items-center gap-3 px-2 pb-1 pt-4 text-center" role="status">
          <span
            className={`grid h-20 w-20 place-items-center rounded-full ${confirmada ? "bg-success/15 text-success" : "bg-danger/10 text-danger"}`}
          >
            {confirmada ? <Check size={42} strokeWidth={2.5} aria-hidden /> : <X size={42} strokeWidth={2.5} aria-hidden />}
          </span>
          <h3 className="text-xl font-extrabold text-text">{tituloResultado}</h3>
          <p className="max-w-xs text-sm text-muted">{textoResultado}</p>
          <button
            type="button"
            onClick={fechar}
            className="mt-3 min-h-11 w-full rounded-xl bg-primary px-4 text-[15px] font-semibold text-on-primary transition-colors hover:bg-primary-hover"
          >
            Concluir
          </button>
        </div>
      </Modal>
    );
  }

  const opcoes: {
    valor: RespostaEscolhida;
    icone: typeof CircleCheck;
    titulo: string;
    descricao: string;
    classeCartao: string;
    classeIcone: string;
    classeMarca: string;
  }[] = [
    {
      valor: "CONFIRMADA",
      icone: CircleCheck,
      titulo: administrativa ? "Confirmou presença" : "Confirmar presença",
      descricao: administrativa ? "Registrar como confirmada" : "Estarei presente",
      classeCartao: "has-[:checked]:border-success has-[:checked]:bg-success/10",
      classeIcone:
        "bg-success/15 text-success group-has-[:checked]:bg-success group-has-[:checked]:text-on-primary dark:group-has-[:checked]:text-bg",
      classeMarca:
        "group-has-[:checked]:border-success group-has-[:checked]:bg-success group-has-[:checked]:text-on-primary dark:group-has-[:checked]:text-bg",
    },
    {
      valor: "RECUSADA",
      icone: CircleX,
      titulo: administrativa ? "Recusou a escala" : "Recusar escala",
      descricao: administrativa ? "Registrar como recusada" : "Não poderei participar",
      classeCartao: "has-[:checked]:border-danger has-[:checked]:bg-danger/10",
      classeIcone:
        "bg-danger/10 text-danger group-has-[:checked]:bg-danger group-has-[:checked]:text-on-primary",
      classeMarca: "group-has-[:checked]:border-danger group-has-[:checked]:bg-danger group-has-[:checked]:text-on-primary",
    },
  ];

  return (
    <Modal titulo={titulo} aberto={aberto} onClose={fechar}>
      <div className="flex flex-col gap-4">
        {administrativa && (
          <p className="flex items-start gap-2.5 rounded-xl bg-primary/10 px-3.5 py-3 text-[13.5px] leading-snug text-text">
            <ShieldCheck size={18} aria-hidden className="mt-px shrink-0 text-primary" />
            <span>Esta resposta será registrada como feita pela liderança, não pela própria pessoa.</span>
          </p>
        )}

        <div className="flex items-center gap-3 rounded-2xl bg-surface-2 p-3">
          {administrativa && nomeParticipante ? (
            <>
              <Avatar nome={nomeParticipante} />
              <div className="min-w-0 flex-1">
                <p className="break-words text-[15px] font-bold leading-tight text-text">{nomeParticipante}</p>
                <p className="text-[13px] text-muted">
                  {funcaoParticipante ? `${funcaoParticipante} · ` : ""}
                  {escalaNome}
                </p>
              </div>
            </>
          ) : (
            <>
              <BlocoData iso={escalaDataHora} />
              <div className="min-w-0 flex-1">
                <p className="break-words text-[15px] font-bold leading-tight text-text">{escalaNome}</p>
                <p className="text-[13px] text-muted">
                  {formatarHora(escalaDataHora)} · {ministerioNome}
                </p>
                {funcaoParticipante && (
                  <span className="mt-1.5 inline-block">
                    <Pill tom="brand">{`Sua função: ${funcaoParticipante}`}</Pill>
                  </span>
                )}
              </div>
            </>
          )}
        </div>

        <fieldset className="flex flex-col gap-2.5">
          <legend className="mb-2.5 text-base font-bold text-text">
            {administrativa ? `Qual foi a resposta de ${primeiroNome}?` : "Você consegue servir nesta escala?"}
          </legend>
          {opcoes.map((o) => {
            const Icone = o.icone;
            return (
              <label
                key={o.valor}
                className={`group flex min-h-[72px] cursor-pointer items-center gap-3.5 rounded-2xl border-2 border-border bg-surface px-3.5 py-3 transition-colors hover:border-muted/60 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary ${o.classeCartao}`}
              >
                <input
                  type="radio"
                  name="resposta-escala"
                  value={o.valor}
                  checked={escolha === o.valor}
                  onChange={() => setEscolha(o.valor)}
                  className="sr-only"
                />
                <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl transition-colors ${o.classeIcone}`}>
                  <Icone size={24} aria-hidden />
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="text-base font-extrabold leading-tight text-text">{o.titulo}</span>
                  <span className="text-[12.5px] leading-snug text-muted">{o.descricao}</span>
                </span>
                <span
                  aria-hidden
                  className={`grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 border-border text-transparent transition-colors ${o.classeMarca}`}
                >
                  <Check size={14} strokeWidth={3} />
                </span>
              </label>
            );
          })}
        </fieldset>

        {escolha === "RECUSADA" && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="justificativa-recusa" className="text-sm font-semibold text-text">
              Motivo da recusa <span className="font-medium text-muted">(obrigatório)</span>
            </label>
            {/* S-3 (ESC-DEC-006): texto da recusa própria; no modo administrativo vale o aviso da liderança acima. */}
            {!administrativa && (
              <p className="text-[12.5px] text-muted">
                A liderança do ministério será avisada da sua recusa. Conte o motivo com clareza para ajudar na
                organização da escala.
              </p>
            )}
            <textarea
              id="justificativa-recusa"
              value={justificativa}
              onChange={(e) => {
                setJustificativa(e.target.value);
                setJustificativaTocada(true);
              }}
              rows={3}
              required
              autoFocus
              aria-describedby="justificativa-recusa-ajuda"
              placeholder={administrativa ? "Motivo informado pela pessoa" : "Ex.: vou viajar nesse fim de semana"}
              className="w-full resize-none rounded-xl border border-transparent bg-surface-2 px-3.5 py-3 text-[15px] text-text placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p
              id="justificativa-recusa-ajuda"
              aria-live="polite"
              className={`min-h-[18px] text-[12.5px] ${faltaJustificativa && justificativaTocada ? "font-semibold text-danger" : "text-muted"}`}
            >
              {faltaJustificativa ? "Escreva o motivo para poder enviar." : ""}
            </p>
          </div>
        )}

        {erro && (
          <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
            {erro}
          </p>
        )}

        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={enviar}
            disabled={!escolha || faltaJustificativa || carregando}
            className={`min-h-11 w-full rounded-xl px-4 text-[15px] font-semibold text-on-primary transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
              escolha === "RECUSADA" ? "bg-danger hover:bg-danger/90" : "bg-primary hover:bg-primary-hover"
            }`}
          >
            {carregando ? "Enviando..." : rotuloBotao}
          </button>
          <BotaoVoltar onClick={fechar} disabled={carregando} className="min-h-11 w-full rounded-xl" />
        </div>
      </div>
    </Modal>
  );
}
